import React, { useState } from 'react';
import { toPlainText } from './HtmlIframe';
import { MessageMarkdown } from './MessageMarkdown';
import { ShareChatDrawer } from './ShareChatDrawer';
import { useChatSession } from '../hooks/useChatSession';

const PRIMARY = '#0E81FF';
const PRIMARY_LIGHT = '#E8F4FF';
const BORDER = '#e5e7eb';

const TOOL_LABELS: Record<string, string> = {
  _connecting: 'Connecting to tools...',
  search_portfolio: 'Searching portfolio...',
  web_search: 'Searching the web...',
  list_companies: 'Listing companies...',
  get_company_profile: 'Looking up company details...',
  get_portfolio_analytics: 'Fetching analytics...',
};

interface AIChatPanelProps {
  /** Optional initial conversation ID to load on mount */
  initialConversationId?: number;
}

export const AIChatPanel: React.FC<AIChatPanelProps> = ({ initialConversationId }) => {
  const {
    messages,
    setMessages,
    isLoading,
    input,
    setInput,
    conversationId,
    setConversationId,
    messagesEndRef,
    sendMessage,
    retryLastMessage,
    startNewConversation,
    conversations,
    loadingConversations,
    toolElapsed,
    copiedId,
    copyText,
  } = useChatSession({
    initialConversationId: initialConversationId ?? null,
    enableToolStatus: true,
    enableConversationList: true,
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: 'inherit' }}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      {/* Sidebar */}
      <div
        style={{
          width: 240,
          borderRight: `1px solid ${BORDER}`,
          display: 'flex',
          flexDirection: 'column',
          background: '#f9fafb',
        }}
      >
        <div style={{ padding: '16px 12px 8px' }}>
          <button
            onClick={startNewConversation}
            style={{
              width: '100%',
              padding: '8px 12px',
              background: PRIMARY,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            + New Chat
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
          {loadingConversations ? (
            <div style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>Loading...</div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>No conversations yet</div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => {
                  setConversationId(conv.id);
                  setMessages([]);
                }}
                style={{
                  padding: '8px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  marginBottom: 2,
                  background: conv.id === conversationId ? PRIMARY_LIGHT : 'transparent',
                  color: conv.id === conversationId ? PRIMARY : '#374151',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {conv.title}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar — only when a conversation is loaded */}
        {conversationId && (
          <div
            style={{
              padding: '8px 16px',
              borderBottom: `1px solid ${BORDER}`,
              display: 'flex',
              justifyContent: 'flex-end',
              background: '#fff',
            }}
          >
            <button
              onClick={() => setShareDrawerOpen(true)}
              title="Share this chat publicly"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
                border: `1px solid ${BORDER}`,
                borderRadius: 6,
                background: '#fff',
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
              Share
            </button>
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '16px 20px' }}>
          {messages.length === 0 && !isLoading && (
            <div
              style={{
                textAlign: 'center',
                color: '#9ca3af',
                marginTop: 80,
                fontSize: 15,
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 12 }}>🤖</div>
              <div>Ask anything about your portfolio</div>
            </div>
          )}
          {messages.map(msg => (
            <div
              key={msg.id}
              style={{
                marginBottom: 16,
                display: 'flex',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                gap: 8,
              }}
            >
              <div style={{ maxWidth: '75%' }}>
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.role === 'user' ? PRIMARY : '#f3f4f6',
                  color: msg.role === 'user' ? '#fff' : '#111827',
                  fontSize: 14,
                  lineHeight: 1.6,
                  overflow: 'hidden',
                  minWidth: 0,
                }}
              >
                {msg.role === 'assistant' && msg.isError ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#b91c1c', marginBottom: 8 }}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      <span style={{ fontSize: 13 }}>{msg.content}</span>
                    </div>
                    <button
                      onClick={retryLastMessage}
                      disabled={isLoading}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 12px', fontSize: 12, fontWeight: 500,
                        border: '1px solid #e5e7eb', borderRadius: 6,
                        background: '#fff', color: '#374151', cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.5 : 1,
                      }}
                    >
                      <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Retry
                    </button>
                  </div>
                ) : msg.role === 'assistant' ? (
                  <MessageMarkdown
                    content={msg.content || (msg.isStreaming ? '▌' : '')}
                    extraComponents={{
                      ul: ({ node, ...props }) => (
                        <ul style={{ paddingLeft: '1.25rem', listStyleType: 'disc', margin: '4px 0' }} {...props} />
                      ),
                      ol: ({ node, ...props }) => (
                        <ol style={{ paddingLeft: '1.25rem', listStyleType: 'decimal', margin: '4px 0' }} {...props} />
                      ),
                      li: ({ node, ...props }) => (
                        <li style={{ marginBottom: '2px' }} {...props} />
                      ),
                      p: ({ node, ...props }) => (
                        <p style={{ margin: '4px 0' }} {...props} />
                      ),
                      a: ({ node, ...props }) => (
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="external link"
                          style={{ color: PRIMARY, textDecoration: 'underline' }}
                          {...props}
                        />
                      ),
                      table: ({ node, ...props }) => (
                        <div style={{ overflowX: 'auto', margin: '8px 0' }}>
                          <table
                            style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: 13,
                            }}
                            {...props}
                          />
                        </div>
                      ),
                      thead: ({ node, ...props }) => (
                        <thead style={{ background: '#f9fafb' }} {...props} />
                      ),
                      th: ({ node, ...props }) => (
                        <th
                          style={{
                            border: `1px solid ${BORDER}`,
                            padding: '6px 10px',
                            textAlign: 'left',
                            fontWeight: 600,
                          }}
                          {...props}
                        />
                      ),
                      td: ({ node, ...props }) => (
                        <td
                          style={{
                            border: `1px solid ${BORDER}`,
                            padding: '6px 10px',
                          }}
                          {...props}
                        />
                      ),
                      // Non-html code chips (MessageMarkdown owns the
                      // ```html``` → HtmlIframe path automatically).
                      code: ({ className, children, ...props }) => (
                        <code
                          style={{
                            background: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 13,
                          }}
                          {...props}
                        >
                          {children}
                        </code>
                      ),
                      pre: ({ node, ...props }) => (
                        <pre
                          style={{
                            background: '#1e1e1e',
                            color: '#d4d4d4',
                            padding: '12px',
                            borderRadius: 8,
                            overflowX: 'auto',
                            fontSize: 13,
                            margin: '8px 0',
                          }}
                          {...props}
                        />
                      ),
                    }}
                  />
                ) : (
                  msg.content
                )}
              </div>
              {msg.role === 'assistant' && msg.toolStatus && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '2px 0',
                    fontSize: 12,
                    color: '#6b7280',
                  }}
                >
                  <span style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>
                    {TOOL_LABELS[msg.toolStatus.tool] || `Running ${msg.toolStatus.tool}...`}
                  </span>
                  {toolElapsed > 0 && <span>({toolElapsed}s)</span>}
                </div>
              )}
              {msg.role === 'assistant' && !msg.isStreaming && !msg.isError && msg.content && (
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <button
                    onClick={() => copyText(`md-${msg.id}`, msg.content)}
                    title="Copy markdown"
                    style={{
                      padding: '3px 10px',
                      fontSize: 12,
                      border: `1px solid ${BORDER}`,
                      borderRadius: 5,
                      background: copiedId === `md-${msg.id}` ? '#d1fae5' : '#fff',
                      color: copiedId === `md-${msg.id}` ? '#065f46' : '#6b7280',
                      cursor: 'pointer',
                    }}
                  >
                    {copiedId === `md-${msg.id}` ? 'Copied!' : 'Copy markdown'}
                  </button>
                  <button
                    onClick={() => copyText(`txt-${msg.id}`, toPlainText(msg.content))}
                    title="Copy as plain text (WhatsApp friendly)"
                    style={{
                      padding: '3px 10px',
                      fontSize: 12,
                      border: `1px solid ${BORDER}`,
                      borderRadius: 5,
                      background: copiedId === `txt-${msg.id}` ? '#d1fae5' : '#fff',
                      color: copiedId === `txt-${msg.id}` ? '#065f46' : '#6b7280',
                      cursor: 'pointer',
                    }}
                  >
                    {copiedId === `txt-${msg.id}` ? 'Copied!' : 'Copy text'}
                  </button>
                </div>
              )}
              </div>
            </div>
          ))}
          {/* Show retry prompt when the last message is from the user with no response */}
          {messages.length > 0 &&
            messages[messages.length - 1].role === 'user' &&
            !isLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
                <button
                  onClick={retryLastMessage}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', fontSize: 13, fontWeight: 500,
                    border: '1px solid #e5e7eb', borderRadius: 8,
                    background: '#fff', color: '#374151', cursor: 'pointer',
                  }}
                >
                  <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry last message
                </button>
              </div>
            )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div
          style={{
            padding: '12px 16px',
            borderTop: `1px solid ${BORDER}`,
            display: 'flex',
            gap: 8,
          }}
        >
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your portfolio... (Enter to send, Shift+Enter for new line)"
            disabled={isLoading}
            rows={2}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              resize: 'none',
              fontSize: 14,
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={() => sendMessage()}
            disabled={isLoading || !input.trim()}
            style={{
              padding: '0 20px',
              background: isLoading || !input.trim() ? '#9ca3af' : PRIMARY,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: 14,
              whiteSpace: 'nowrap',
            }}
          >
            {isLoading ? 'Thinking...' : 'Send'}
          </button>
        </div>
      </div>

      <ShareChatDrawer
        isOpen={shareDrawerOpen}
        onClose={() => setShareDrawerOpen(false)}
        conversationId={conversationId == null ? null : Number(conversationId)}
      />
    </div>
  );
};
